import { useSyncExternalStore } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { DevClubLogo } from '@/components/ui/DevClubLogo';
import { Atmosfera } from '@/components/auth/Atmosfera';
import { CampoDeCruzes } from '@/components/auth/CampoDeCruzes';
import { EmployerLogoCarousel } from '@/components/auth/EmployerLogoCarousel';
import { BrandRail } from '@/components/brand/BrandMark';
import { Button } from '@/components/ui/button';
import { usePlatformTheme } from '@/hooks/usePlatformTheme';
import { Sun, Moon } from 'lucide-react';
import styles from './AuthLayout.module.css';

/** Mesmo corte do CSS (max-width: 767px). */
const PAINEL_DE_FOTO = '(min-width: 768px)';

/**
 * No celular o painel de foto nem é montado: escondido só por CSS ele ainda
 * baixaria a foto e os logos das empresas (cerca de 1 MB).
 */
function useMostraPainelDeFoto() {
  return useSyncExternalStore(
    (avisa) => {
      const consulta = window.matchMedia(PAINEL_DE_FOTO);
      consulta.addEventListener('change', avisa);
      return () => consulta.removeEventListener('change', avisa);
    },
    () => window.matchMedia(PAINEL_DE_FOTO).matches,
  );
}

/**
 * Tela de entrada: o painel de fotografia da versão anterior (marcas DevClub,
 * IAClub e Works AI, chamada e empresas que contrataram alunos) com a
 * identidade do Juan no lado do formulário: atmosfera, malha de cruzes, logo
 * vivo e o cartão translúcido. No celular ficam só o logo e o cartão.
 */
export function AuthLayout() {
  const { theme, toggleTheme } = usePlatformTheme();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { pathname } = useLocation();
  const isLogin = pathname === '/login';
  const mostraPainel = useMostraPainelDeFoto();

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const ajuda = (
    <div className={styles.formTools}>
      <a
        href="https://go.rodolfomori.com.br/suporte"
        target="_blank"
        rel="noopener noreferrer"
      >
        Precisa de ajuda?
      </a>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
        onClick={toggleTheme}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
      </Button>
    </div>
  );

  return (
    <div className={styles.layout}>
      <Atmosfera />
      <CampoDeCruzes />

      {mostraPainel && (
        <aside
          className={cn(styles.visual, isLogin && styles.visualWithEmployers)}
          aria-label="DevClub, IAClub e Works AI"
        >
          {/* Foto vertical feita para este painel: objetos no alto, base
              escura livre para a chamada e as empresas. */}
          <img
            className={styles.studioArt}
            src="/brand/member-login-studio.webp"
            alt=""
            fetchPriority="high"
          />
          <div className={styles.photoShade} aria-hidden="true" />
          <BrandRail className={styles.visualBrand} />
          <div className={styles.visualCopy}>
            <h2>
              Conhecimento
              <br />
              que vira projeto.
            </h2>
            <p>
              Programação, inteligência artificial e negócios.
              <br />
              Aprenda. Coloque em prática. Vá além.
            </p>
            {isLogin && <EmployerLogoCarousel />}
          </div>
        </aside>
      )}

      <main id="auth-content" className={styles.formSide}>
        {/* Luz verde parada atrás do cartão */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="bg-primary/[0.07] absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
        </div>

        {mostraPainel && ajuda}

        <div className={styles.formContent}>
          <div className="mx-auto w-full max-w-md">
            <div className="animate-fade-in mb-6 text-center sm:mb-8">
              <div className="mb-3 flex justify-center sm:mb-5">
                <DevClubLogo size="lg" showText vivo />
              </div>
              <p className="text-muted-foreground hidden text-base md:block">
                A escola de Tecnologia que transforma carreiras
              </p>
            </div>
            <div
              className="animate-fade-in-up flex justify-center"
              style={{ animationDelay: '100ms' }}
            >
              <Outlet />
            </div>
          </div>
        </div>

        {/* No celular a ajuda vem depois do cartão, na tela e no Tab. */}
        {!mostraPainel && ajuda}

        <p className={styles.formFootnote}>Aprender. Construir. Pertencer.</p>
      </main>
    </div>
  );
}
